// Mantis #1453 — M001-D029 §5/§6, contract SAPI-D029-004
// (interfaces/core-realmauthority-editor/v1, vendored at
// tests/realmauthority/pinned/). Same-origin Core BFF only (D-M): the
// browser never learns a RealmAuthority URL, App Key or Installation UUID —
// those live Core-side.
//
// Kept as its own module (not appended to services/api.js) so the #1371
// same-MR repair of the shared service-links surface and this editor stay on
// disjoint module paths until the parent confirms the corrected base.
//
// Wire discipline (M001-D029 §4/§6, contract idScheme rulings 1-5):
// - CARDINALITY IS 1:1 — one RealmAuthority realm per organisation. Every
//   path below is exactly /api/v1/orgs/:orgId/realm-authority[...]; there is
//   no installationId/realmId anywhere in a URL or a body. The server
//   resolves the org's single bound realm itself.
// - the only person identifier on the wire is objectSid (never a Core
//   users.uuid, never a card code, never a display name);
// - the only verb is a membership add/remove batch of already-published AD
//   people on an already-published group (no create/provision op exists);
// - submitGroupMembershipChanges takes ONE request body {changes:[{objectSid,
//   op}]}, up to 50 changes, and returns one resolved MembershipChangeOutcome
//   per change in the same order — there is no client-supplied
//   Idempotency-Key on this contract (Core mints its own commandId
//   server-side; see interface.json's idempotency note).
import axios from 'axios';
import config from '../config';
import { getAccessToken } from './authStorage';
import { EDITOR_CONTRACT } from './realmAuthorityEditorContract.generated';

const MAX_CHANGES_PER_BATCH = 50;

const instance = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth token only. Deliberately NO 401 redirect interceptor: a 401 on this
// surface is a SAL-4 step-up challenge (F0308) that the editor answers with
// StepUpDialog — never a session loss that should bounce the admin to /login.
instance.interceptors.request.use((reqConfig) => {
  const token = getAccessToken();
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`;
  }
  return reqConfig;
});

// NOTE: config.apiBaseUrl already supplies the leading /api (default '/api',
// see src/config/index.js and nginx.conf's `location /api/` proxy) — these
// paths are relative to that baseURL, so the RESOLVED request URL matches
// the contract's canonical route (/api/v1/orgs/:orgId/realm-authority/...)
// exactly. Do not add a second leading /api here.
//
// Every path below is SUBSTITUTED from the contract's own declared
// canonicalRoutes (src/services/realmAuthorityEditorContract.generated.js,
// regenerated from the digest-pinned bytes by
// tests/realmauthority/bind-editor-contract.mjs) — no endpoint string is
// hand-typed where the contract declares an operation. driftRoute throws
// rather than guessing if the generated module ever stops declaring an op.
const substituteRoute = (route, params) => {
  const resolved = route.replace(/\{(\w+)\}/g, (_, name) => {
    const value = params[name];
    if (value === undefined || value === null || value === '') {
      throw new Error(`declared route ${route} requires a non-empty {${name}}`);
    }
    return encodeURIComponent(value);
  });
  return resolved.replace(/^\/api/, '');
};
const declaredRoute = (operationId) => {
  const route = EDITOR_CONTRACT.canonicalRoutes[operationId];
  if (!route) throw new Error(`contract ${EDITOR_CONTRACT.contract} v${EDITOR_CONTRACT.contractVersion} declares no canonical route for ${operationId}`);
  return route;
};
const linkPath = (orgId) => substituteRoute(declaredRoute('getRealmAuthorityLinkStatus'), { orgId });
const groupsPath = (orgId) => substituteRoute(declaredRoute('getRealmAuthorityGroups'), { orgId });
const directoryPath = (orgId) => substituteRoute(declaredRoute('getRealmAuthorityDirectory'), { orgId });
const membershipPath = (orgId, groupSid) => substituteRoute(
  declaredRoute('submitGroupMembershipChanges'),
  { orgId, groupSid },
);
// MAD001-F0236: the submitPersonRename operation (1.1.0). Same 1:1
// cardinality and objectSid-only subject discipline as every path above —
// the person is addressed by objectSid alone, never by a Core identity or a
// display name. The declared op carries the request type
// (PersonRenameRequest: exactly {displayName}) and the response type
// (PersonRenameData: {result: PersonRenameOutcome, lastSnapshotAt}) this
// client sends and projects.
const personRenamePath = (orgId, objectSid) => substituteRoute(
  declaredRoute('submitPersonRename'),
  { orgId, objectSid },
);

// M004-F0698: the 1.2.0 read of the recorded door outcome of recent
// membership commands (getRealmAuthorityMembershipDoorOutcomes, RA #1579).
const membershipOutcomesPath = (orgId, groupSid) => substituteRoute(
  declaredRoute('getRealmAuthorityMembershipDoorOutcomes'),
  { orgId, groupSid },
);

export const realmAuthorityGroupsApi = {
  // getRealmAuthorityLinkStatus (SAPI-D029-004): linked/installationUuid/
  // tunnelConnected/lastSnapshotAt. "No realm linked" is an explicit 200
  // (linked:false), never a 404 — never inferred from any other field.
  getLinkStatus: (organisationId, options = {}) => instance.get(
    linkPath(organisationId),
    { signal: options.signal },
  ),
  // getRealmAuthorityGroups: the mirrored, opted-in groups view.
  getGroups: (organisationId, options = {}) => instance.get(
    groupsPath(organisationId),
    { signal: options.signal },
  ),
  // getRealmAuthorityDirectory: the ONLY eligible picker source.
  getDirectory: (organisationId, options = {}) => instance.get(
    directoryPath(organisationId),
    { signal: options.signal },
  ),
  // submitGroupMembershipChanges: one POST per Save, carrying the WHOLE
  // batch of staged adds/removes for one group. Max 50 changes per the
  // contract; callers must split a larger draft themselves (this editor
  // never stages more than 50 at once).
  submitMembershipChanges: (organisationId, groupSid, changes) => {
    if (!Array.isArray(changes) || !changes.length) {
      return Promise.reject(new Error('submitMembershipChanges requires at least one change'));
    }
    if (changes.length > MAX_CHANGES_PER_BATCH) {
      return Promise.reject(new Error(`submitMembershipChanges accepts at most ${MAX_CHANGES_PER_BATCH} changes per batch`));
    }
    return instance.post(
      membershipPath(organisationId, groupSid),
      { changes: changes.map(({ objectSid, op }) => ({ objectSid, op })) },
    );
  },
  // getRealmAuthorityMembershipDoorOutcomes (editor 1.2.0): up to 50 commands
  // of the last 24h for ONE group, newest first, each with RealmAuthority's
  // door-write outcome (null until it arrives). access_control_view, a plain
  // read: no step-up. The caller correlates by commandId; see
  // useDoorOutcomePoll and normalisers.projectMembershipDoorOutcomes.
  getMembershipOutcomes: (organisationId, groupSid, options = {}) => {
    const declared = EDITOR_CONTRACT.operations.getRealmAuthorityMembershipDoorOutcomes;
    if (!declared || declared.method !== 'GET') {
      return Promise.reject(new Error('declared operation getRealmAuthorityMembershipDoorOutcomes is not a GET'));
    }
    return instance.get(membershipOutcomesPath(organisationId, groupSid), { signal: options.signal });
  },
  // MAD001-F0236: rename one published AD person via the DECLARED
  // submitPersonRename operation (editor contract 1.1.0). ONE command per
  // call — Core dispatches it single-shot over its tunnel and resolves from
  // the app's ack (same 3s SLA semantics as submitMembershipChanges). The
  // request is exactly the declared PersonRenameRequest — {displayName},
  // additionalProperties:false — trimmed, 1..128, enforced here as a
  // client-side precheck (the server re-enforces it; the drawer never sends
  // whitespace). The response projects through the declared PersonRenameData
  // ({result: PersonRenameOutcome, lastSnapshotAt}) in normalisers.js.
  renamePerson: (organisationId, objectSid, displayName) => {
    const name = typeof displayName === 'string' ? displayName.trim() : '';
    if (!name) {
      return Promise.reject(new Error('renamePerson requires a non-empty displayName'));
    }
    if (name.length > 128) {
      return Promise.reject(new Error('renamePerson accepts at most 128 characters'));
    }
    const declared = EDITOR_CONTRACT.operations.submitPersonRename;
    if (declared.method !== 'POST') {
      return Promise.reject(new Error(`declared operation submitPersonRename is ${declared.method}, not POST`));
    }
    return instance.post(personRenamePath(organisationId, objectSid), { displayName: name });
  },
};

export { MAX_CHANGES_PER_BATCH };
